import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, 
  Trophy, 
  Calendar, 
  AlertTriangle, 
  Shield, 
  Ban,
  Clock,
  TrendingUp,
  X,
  History
} from 'lucide-react';
import { SanctionDialog } from './SanctionDialog';
import type { InvestigatedPlayer, PlayerSanction } from '@/hooks/useAdminInvestigation';
import { format } from 'date-fns';

interface CheatingInvestigationPanelProps {
  player: InvestigatedPlayer;
  onClose: () => void;
  onIssueSanction: (data: any) => Promise<void>;
  onRevokeSanction: (sanctionId: string, reason: string) => Promise<void>;
}

export function CheatingInvestigationPanel({
  player,
  onClose,
  onIssueSanction,
  onRevokeSanction
}: CheatingInvestigationPanelProps) {
  const [sanctionDialogOpen, setSanctionDialogOpen] = useState(false);

  const activeSanctions = player.sanctions.filter(s => s.is_active);
  const winRate = player.games_played > 0 
    ? ((player.games_won / player.games_played) * 100).toFixed(1) 
    : 0;

  // Calculate average suspicion score from analyses
  const avgSuspicion = player.gameAnalyses.length > 0
    ? Math.round(
        player.gameAnalyses.reduce((sum, a) => sum + (a.overall_suspicion_score || 0), 0) / 
        player.gameAnalyses.length
      )
    : null;

  const getSanctionBadge = (sanction: PlayerSanction) => {
    const colors = {
      warning: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
      suspension: 'bg-red-500/10 text-red-500 border-red-500/30',
      ban: 'bg-destructive/10 text-destructive border-destructive/30'
    };
    return colors[sanction.sanction_type];
  };

  return (
    <>
      <Card className="border-primary/30">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {player.display_name || player.username || 'Unknown Player'}
                </CardTitle>
                <p className="text-sm text-muted-foreground">@{player.username}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <Trophy className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-bold">{player.elo_rating}</p>
              <p className="text-xs text-muted-foreground">ELO</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <TrendingUp className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-bold">{winRate}%</p>
              <p className="text-xs text-muted-foreground">Win Rate</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg text-center">
              <History className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-bold">{player.games_played}</p>
              <p className="text-xs text-muted-foreground">Games</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${
              avgSuspicion !== null && avgSuspicion >= 70 
                ? 'bg-destructive/10' 
                : avgSuspicion !== null && avgSuspicion >= 40 
                  ? 'bg-orange-500/10' 
                  : 'bg-green-500/10'
            }`}>
              <AlertTriangle className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-lg font-bold">{avgSuspicion ?? '-'}</p>
              <p className="text-xs text-muted-foreground">Avg Suspicion</p>
            </div>
          </div>

          {/* Active Sanctions Alert */}
          {activeSanctions.length > 0 && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Ban className="h-4 w-4 text-destructive" />
                <span className="font-medium text-destructive">Active Sanctions</span>
              </div>
              <div className="space-y-1">
                {activeSanctions.map(sanction => (
                  <div key={sanction.id} className="flex items-center justify-between text-sm">
                    <Badge className={getSanctionBadge(sanction)}>
                      {sanction.sanction_type}
                    </Badge>
                    {sanction.expires_at && (
                      <span className="text-xs text-muted-foreground">
                        Expires: {format(new Date(sanction.expires_at), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="games" className="w-full">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="games">Games ({player.recentGames.length})</TabsTrigger>
              <TabsTrigger value="analyses">Analyses ({player.gameAnalyses.length})</TabsTrigger>
              <TabsTrigger value="history">History ({player.sanctions.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="games" className="mt-3">
              <div className="max-h-48 overflow-y-auto space-y-2">
                {player.recentGames.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No completed games found
                  </p>
                ) : (
                  player.recentGames.map(game => (
                    <div 
                      key={game.id} 
                      className="p-2 bg-muted/30 rounded-lg flex items-center justify-between text-sm"
                    >
                      <div>
                        <p className="font-mono text-xs">{game.id.slice(0, 8)}...</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(game.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {game.winner === player.id ? 'Won' : 'Lost'}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="analyses" className="mt-3">
              <div className="max-h-48 overflow-y-auto space-y-2">
                {player.gameAnalyses.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No cached analyses. Run analysis on specific games.
                  </p>
                ) : (
                  player.gameAnalyses.map(analysis => (
                    <div 
                      key={analysis.id} 
                      className={`p-2 rounded-lg flex items-center justify-between text-sm ${
                        (analysis.overall_suspicion_score || 0) >= 70 
                          ? 'bg-destructive/10' 
                          : (analysis.overall_suspicion_score || 0) >= 40 
                            ? 'bg-orange-500/10' 
                            : 'bg-muted/30'
                      }`}
                    >
                      <div>
                        <p className="font-mono text-xs">{analysis.game_session_id.slice(0, 8)}...</p>
                        <p className="text-xs text-muted-foreground">
                          Engine: {analysis.engine_correlation_score}% | 
                          CPL: {analysis.avg_centipawn_loss}
                        </p>
                      </div>
                      <Badge className={
                        (analysis.overall_suspicion_score || 0) >= 70 
                          ? 'bg-destructive/10 text-destructive' 
                          : (analysis.overall_suspicion_score || 0) >= 40 
                            ? 'bg-orange-500/10 text-orange-500' 
                            : 'bg-green-500/10 text-green-500'
                      }>
                        {analysis.overall_suspicion_score}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-3">
              <div className="max-h-48 overflow-y-auto space-y-2">
                {player.sanctions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No sanction history
                  </p>
                ) : (
                  player.sanctions.map(sanction => (
                    <div 
                      key={sanction.id} 
                      className={`p-2 rounded-lg text-sm ${
                        sanction.is_active ? 'bg-destructive/5' : 'bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={getSanctionBadge(sanction)}>
                          {sanction.sanction_type}
                          {!sanction.is_active && ' (revoked)'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(sanction.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{sanction.reason}</p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => setSanctionDialogOpen(true)}
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              Warning
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-red-500 border-red-500/30 hover:bg-red-500/10"
              onClick={() => setSanctionDialogOpen(true)}
            >
              <Shield className="h-4 w-4 mr-1" />
              Suspend
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              className="flex-1"
              onClick={() => setSanctionDialogOpen(true)}
            >
              <Ban className="h-4 w-4 mr-1" />
              Ban
            </Button>
          </div>
        </CardContent>
      </Card>

      <SanctionDialog
        open={sanctionDialogOpen}
        onOpenChange={setSanctionDialogOpen}
        playerName={player.display_name || player.username || 'Player'}
        playerId={player.id}
        onConfirm={onIssueSanction}
      />
    </>
  );
}
